import { extractMetricKeys } from "./formulaTokenizer"

interface ValidationResult {
  isValid: boolean
  error?: string // i18n key
  errorParams?: Record<string, string | number>
}

const OPERATORS = ["+", "-", "*", "/", "×", "÷"]
const PARENS = ["(", ")"]

/**
 * 토큰이 값(메트릭 또는 숫자)인지 확인
 */
function isValue(token: string): boolean {
  if (!token) return false
  if (OPERATORS.includes(token) || PARENS.includes(token)) return false
  return true
}

/**
 * 수식 유효성 검사 (전체)
 */
export function validateFormula(formula: string): ValidationResult {
  const trimmed = formula.trim()

  if (!trimmed) {
    return { isValid: false, error: "rules.required" }
  }

  // 1. 메트릭 개수 체크 (명확한 에러)
  const countResult = validateMetricCount(trimmed)
  if (!countResult.isValid) return countResult

  // 2. 수식 구조 체크 (나머지는 invalidFormula로 통일)
  const structureResult = validateFormulaStructure(trimmed)
  if (!structureResult.isValid) return structureResult

  return { isValid: true }
}

/**
 * 수식 구조 검증 (괄호, 연산자, 값 순서 등)
 */
function validateFormulaStructure(formula: string): ValidationResult {
  // 괄호 쌍 체크
  let parenCount = 0
  for (const char of formula) {
    if (char === "(") parenCount++
    if (char === ")") parenCount--
    if (parenCount < 0) {
      return { isValid: false, error: "rules.error" }
    }
  }
  if (parenCount !== 0) {
    return { isValid: false, error: "rules.error" }
  }

  // 토큰 단위 분석
  const tokens = formula.replace(/\s+/g, " ").trim().split(" ").filter(Boolean)

  if (tokens.length === 0) {
    return { isValid: false, error: "rules.error" }
  }

  for (let i = 0; i < tokens.length; i++) {
    const current = tokens[i]
    const next = tokens[i + 1]

    const isCurrentOp = OPERATORS.includes(current)
    const isCurrentValue = isValue(current)
    const isCurrentOpenParen = current === "("
    const isCurrentCloseParen = current === ")"

    // 다음 토큰 분석
    if (next) {
      const isNextOp = OPERATORS.includes(next)
      const isNextValue = isValue(next)
      const isNextCloseParen = next === ")"

      // 연산자 연속 (+ + 같은 경우)
      if (isCurrentOp && isNextOp) {
        return { isValid: false, error: "rules.error" }
      }

      // 값 연속 (metric metric, metric 2, 2 metric 같은 경우)
      if (isCurrentValue && isNextValue) {
        return { isValid: false, error: "rules.error" }
      }

      // 여는 괄호 바로 뒤에 연산자 ( + 같은 경우)
      if (isCurrentOpenParen && isNextOp) {
        return { isValid: false, error: "rules.error" }
      }

      // 연산자 바로 뒤에 닫는 괄호 + ) 같은 경우
      if (isCurrentOp && isNextCloseParen) {
        return { isValid: false, error: "rules.error" }
      }
    }

    // 첫 토큰이 연산자 (괄호 제외)
    if (i === 0 && isCurrentOp) {
      return { isValid: false, error: "rules.error" }
    }

    // 마지막 토큰이 연산자
    if (i === tokens.length - 1 && isCurrentOp) {
      return { isValid: false, error: "rules.error" }
    }

    // 마지막 토큰이 여는 괄호
    if (i === tokens.length - 1 && isCurrentOpenParen) {
      return { isValid: false, error: "rules.error" }
    }

    // 첫 토큰이 닫는 괄호
    if (i === 0 && isCurrentCloseParen) {
      return { isValid: false, error: "rules.error" }
    }
  }

  return { isValid: true }
}

/**
 * 메트릭 개수 검증 (최대 10개)
 */
export function validateMetricCount(formula: string): ValidationResult {
  const keys = extractMetricKeys(formula)
  if (keys.length > 10) {
    return { isValid: false, error: "messages.metricCountExceeded", errorParams: { count: 10 } }
  }
  return { isValid: true }
}

/**
 * 이름 유효성 검사
 */
export function validateName(name: string, existingNames: string[]): ValidationResult {
  const trimmed = name.trim()

  if (!trimmed) {
    return { isValid: false, error: "rules.required" }
  }

  if (trimmed.length > 35) {
    return { isValid: false, error: "rules.max_len", errorParams: { max_len: 35 } }
  }

  // 중복 체크 (대소문자 무시)
  const isDuplicate = existingNames.some(
    (existing) => existing.toLowerCase() === trimmed.toLowerCase(),
  )
  if (isDuplicate) {
    return { isValid: false, error: "messages.108" }
  }

  return { isValid: true }
}

/**
 * 수식 계산 (Test Formula용)
 * 안전한 수식 평가를 위해 직접 파싱
 */
export function evaluateFormula(formula: string, values: Record<string, number>): number | null {
  try {
    if (!formula.trim()) return null

    // 메트릭 키를 값으로 치환
    let expression = formula
    for (const [key, value] of Object.entries(values)) {
      // 전체 단어 매칭으로 치환 (부분 매칭 방지)
      const regex = new RegExp(`\\b${escapeRegex(key)}\\b`, "g")
      expression = expression.replace(regex, String(value))
    }

    // 아직 치환되지 않은 메트릭이 있는지 확인
    const remaining = expression.match(/[a-zA-Z_][a-zA-Z0-9_]*/g)
    if (remaining && remaining.length > 0) {
      return null // 모든 메트릭 값이 입력되지 않음
    }

    // × → *, ÷ → / 변환
    expression = expression.replace(/×/g, "*").replace(/÷/g, "/")

    // 안전한 수식 평가 (숫자, 연산자, 괄호만 허용)
    const sanitized = expression.replace(/[^0-9+\-*/().\s]/g, "")
    // 공백 제거 후 비교하여 유효하지 않은 문자 확인
    if (sanitized.replace(/\s/g, "") !== expression.replace(/\s/g, "")) {
      return null // 유효하지 않은 문자 포함
    }

    // Function constructor를 사용한 안전한 평가
    const result = new Function(`return (${sanitized})`)()

    if (typeof result !== "number" || !isFinite(result)) {
      return null
    }

    return Math.round(result * 1000) / 1000 // 소수점 3자리까지
  } catch {
    return null
  }
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}
